import React, { useEffect, useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import DialogConfirm from '../controls/dialogs/DialogConfirm';

import { LayerConfiguration } from '../../models/maps/layerConfiguration';
import { LayerType } from '../../models/types/layerType';

import { planManagementService } from '../../services/plan-management.service';
import { authService } from '../../services/auth.service';


const VisibilityLayersActionMenu = ({ 
  params,
  layerType,
  onRefresh
}: {
  params: GridRenderCellParams<LayerConfiguration>;
  layerType: LayerType;
  onRefresh: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLayerConfiguratieId, setSelectedLayerConfiguratie] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpenDialogs] = useState({
    visibility: false,
    visibility_off: false
  });

  useEffect(() => {
    if (params?.value?.id) {
      setSelectedLayerConfiguratie(params?.value?.id);
    }
  }, []);

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    handleCloseMenu();
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
    setSelectedLayerConfiguratie(null);
  };
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedLayerConfiguratie(id) // id: string
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleToggleActive = async () => {
    handleCloseMenu();
    if (!selectedLayerConfiguratieId) return;

    try {
      setLoading(true);
  
      let layerConfiguratie;
      if (params?.row?.canUse) {
        layerConfiguratie = await planManagementService.disableLayerConfiguration(layerType, selectedLayerConfiguratieId);
      }
      else {
        layerConfiguratie = await planManagementService.enableLayerConfiguration(layerType, selectedLayerConfiguratieId);
      }

      if (!layerConfiguratie) {
        throw new Error("Layer configuratie niet gevonden");
      }

      closeDialog(params.row.canUse ? 'visibility_off' : 'visibility');
      onRefresh();
    } 
    catch (error) {
      console.error("Error toggling active:", error);
      setError("Er is een fout opgetreden bij het wijzigen van de status");
    } 
    finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      {authService.isKeyUser() && (
        <IconButton onClick={(e) => handleOpenMenu(e, params.id as string)}>
          <Tooltip title="Acties" placement="right" arrow>
            <Icon>more_vert</Icon>
          </Tooltip>
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { openDialog(params.row.canUse ? 'visibility_off' : 'visibility') }}>
          <ListItemIcon>
            <Icon color={params.row.canUse ? 'disabled' : 'success'}>
              {params.row.canUse ? "toggle_off" : "toggle_on"}
            </Icon>
          </ListItemIcon>
          {params.row.canUse ? "Niet zichtbaar maken" : "Zichtbaar maken"}
        </MenuItem>
      </Menu>

      <DialogConfirm
        open={open.visibility}
        title={'Kaartlaag zichtbaar maken'}
        contentText={'Weet je zeker dat je deze kaart zichtbaar wilt maken?'}
        confirmButtonText={'Ja dat weet ik zeker'}
        onClose={() => { closeDialog('visibility') }}
        onConfirm={handleToggleActive}
      />

      <DialogConfirm
        open={open.visibility_off}
        title={'Kaartlaag niet zichtbaar maken'}
        contentText={'Weet je zeker dat je deze kaart niet zichtbaar wilt maken?'}
        confirmButtonText={'Ja dat weet ik zeker'}
        onClose={() => { closeDialog('visibility_off') }}
        onConfirm={handleToggleActive}
      />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default VisibilityLayersActionMenu;
